import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X, Loader2, CheckCircle2, FileText, QrCode, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { printTicket80mm } from '../../utils/ticket80mmPrint';

const SIZE_OPTIONS = [
  // Phase 2.4.4 — only the 50x30mm Bixolon format is supported.
  { value: '50x30mm', label: '50 x 30 mm (standard Bixolon)' },
];

const TABS = {
  ticket: { label: 'Registre 80mm', icon: Printer },
  fiche: { label: 'Fiche PDF', icon: FileText },
  qr: { label: 'Etiquette QR', icon: QrCode },
};

const normalizeTab = (mode) => (mode === 'fiche' || mode === 'ticket' || mode === 'qr' ? mode : 'ticket');

/**
 * Phase 2.1 - PostSaveRepairModal
 * Modes: fiche (PDF A4), ticket (thermique / PDF), qr (étiquette Bixolon).
 * Used after save AND from list buttons (ReparationsPage).
 */
const PostSaveRepairModal = ({ open, deviceRef, defaultMode = 'ticket', onClose }) => {
  const { api } = useAuth();
  const [tab, setTab] = useState(normalizeTab(defaultMode));
  const [size, setSize] = useState('50x30mm');
  const [printing, setPrinting] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Authenticated PNG preview: fetch as blob and create object URL
  useEffect(() => {
    if (!open || tab !== 'qr' || !deviceRef) return;
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const res = await api.get(`/printer/preview`, {
          params: { ref: deviceRef, format: size },
          responseType: 'blob',
        });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setPreviewUrl(url);
      } catch (_e) {
        if (!cancelled) setPreviewUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, tab, deviceRef, size, previewKey, api]);

  useEffect(() => {
    if (!open) return;
    setTab(normalizeTab(defaultMode));
    setDone(null);
    setError(null);
    // Pre-fill size with admin default
    (async () => {
      try {
        const res = await api.get('/printer/settings');
        const fmt = res?.data?.label_format;
        if (fmt && SIZE_OPTIONS.some((o) => o.value === fmt)) setSize(fmt);
        else setSize('50x30mm');
      } catch (_e) { /* keep default */ }
    })();
  }, [open, defaultMode, api]);

  const aspectRatio = useMemo(() => {
    const map = { "50x30mm": "50 / 30", "1.1x2.4": "11 / 24", "1.7x2.4": "17 / 24", "2.4x2.4": "1 / 1", "2.4x4": "24 / 40" };
    return map[size] || "50 / 30";
  }, [size]);

  if (!open) return null;

  const handleQueueZpl = async () => {
    if (!deviceRef) return;
    setQueueing(true);
    setError(null);
    setDone(null);
    try {
      const res = await api.post('/printer/jobs', { ref: deviceRef, size });
      setDone(`Job envoye au Bixolon (${res?.data?.bytes || '?'} octets ZPL).`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur queue Bixolon');
    } finally {
      setQueueing(false);
    }
  };

  const handlePrintPdf = async () => {
    if (!deviceRef) return;
    setPrinting(true);
    setError(null);
    setDone(null);
    try {
      const res = await api.get(`/devices/${deviceRef}/qr-label`, {
        params: { size }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (w) { try { w.focus(); } catch (_) {} setTimeout(() => { try { w.print(); } catch (_) {} }, 700); }
      setDone('PDF ouvert dans un nouvel onglet.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur PDF');
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintFichePdf = async () => {
    if (!deviceRef) return;
    setPrinting(true);
    setError(null);
    setDone(null);
    try {
      const res = await api.get(`/devices/${deviceRef}/fiche-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (w) { try { w.focus(); } catch (_) {} setTimeout(() => { try { w.print(); } catch (_) {} }, 700); }
      setDone('Fiche PDF ouverte dans un nouvel onglet.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur fiche PDF');
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintTicketThermal = async () => {
    if (!deviceRef) return;
    setQueueing(true);
    setError(null);
    setDone(null);
    try {
      const data = await printTicket80mm(api, { type: 'repair', ref: deviceRef, quiet: true });
      const id = data?.job?.id || data?.job_id;
      setDone(`Registre complet envoyé à l'Epson 80mm${id ? ` (${String(id).slice(0, 8)}…)` : ''}.`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur queue ticket thermique');
    } finally {
      setQueueing(false);
    }
  };

  const handleClose = () => onClose?.({ printed: !!done });

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="post-save-repair-modal"
      onClick={handleClose}
    >
      <div className="bg-[#151820] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Printer className="w-5 h-5 text-red-400" />
            Imprimer · <span className="font-mono text-red-400" data-testid="post-save-modal-ref">{deviceRef || '-'}</span>
          </h3>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-white p-1" aria-label="Fermer" data-testid="post-save-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {Object.entries(TABS).map(([k, t]) => {
            const Icon = t.icon;
            const active = tab === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs sm:text-sm font-medium transition-colors ${active ? 'text-white border-b-2 border-red-500 bg-white/5' : 'text-gray-400 hover:text-white'}`}
                data-testid={`post-save-tab-${k}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </div>

        <div className="px-6 py-5 space-y-4">
          {tab === 'fiche' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-300">
                Fiche de dépôt complète (pièces cochées, prix, conditions, signatures) — même contenu que le registre 80mm.
              </p>
              <ul className="text-xs text-gray-500 list-disc list-inside space-y-1">
                <li>Format A4 — impression navigateur ou PDF.</li>
                <li>Document remis au client à la prise en charge.</li>
              </ul>
              {error && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}
              {done && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {done}</div>}
            </div>
          )}

          {tab === 'qr' && (
            <>
              <div>
                <label className="block text-sm text-gray-300 mb-1.5" htmlFor="qr-label-size">
                  Format etiquette
                </label>
                <select
                  id="qr-label-size"
                  data-testid="post-save-modal-size"
                  value={size}
                  onChange={(e) => { setSize(e.target.value); setPreviewKey((k) => k + 1); }}
                  disabled={queueing || printing}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-red-500/50"
                >
                  {SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div className="bg-white rounded-lg p-2 border border-white/10" data-testid="post-save-modal-preview">
                <div className="mx-auto" style={{ aspectRatio, width: '100%', maxWidth: 360 }}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="apercu etiquette" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation de l'apercu...
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>
              )}
              {done && (
                <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {done}
                </div>
              )}
            </>
          )}

          {tab === 'ticket' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-300">
                Registre complet remis au client sur imprimante Epson 80mm : pièces signalées, prix, conditions générales, signatures et QR suivi.
              </p>
              <p className="text-xs text-amber-300/90">
                Ce n&apos;est pas la petite étiquette QR — onglet « Etiquette QR » séparé.
              </p>
              {error && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}
              {done && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {done}</div>}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/10 flex-wrap">
          <button type="button" onClick={handleClose} className="px-4 py-2 rounded-lg text-gray-300 hover:bg-white/5" data-testid="post-save-modal-skip-btn" disabled={printing || queueing}>
            Fermer
          </button>
          {tab === 'fiche' && (
            <button
              type="button"
              onClick={handlePrintFichePdf}
              disabled={printing || queueing || !deviceRef}
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="post-save-modal-fiche-btn"
            >
              {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Imprimer fiche PDF
            </button>
          )}
          {tab === 'qr' && (
            <>
              <button
                type="button"
                onClick={handlePrintPdf}
                disabled={printing || queueing || !deviceRef}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="post-save-modal-pdf-btn"
              >
                {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                PDF (navigateur)
              </button>
              <button
                type="button"
                onClick={handleQueueZpl}
                disabled={printing || queueing || !deviceRef}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="post-save-modal-print-btn"
              >
                {queueing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Imprimer (Bixolon)
              </button>
            </>
          )}
          {tab === 'ticket' && (
            <button
              type="button"
              onClick={handlePrintTicketThermal}
              disabled={printing || queueing || !deviceRef}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="post-save-modal-ticket-thermal-btn"
            >
              {queueing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              Imprimer registre complet
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PostSaveRepairModal;
